<style>
@import '~@/assets/css/vue-tour-overrides.css';
</style>
<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <section id="app-wrapper" :class="{'is-electron': isElectron}">
      <component :is="layout">
        <router-view />
      </component>
      <div id="tour-welcome-anchor" style="position: fixed; top: 50%; left: 50%; width: 0; height: 0;" />
      <div id="tour-post-view-anchor" style="position: fixed; top: 50%; left: 50%; width: 0; height: 0;" />
      <div
        v-if="isOnboarding"
        class="onboarding-click-shield"
        @click.prevent.stop
        @contextmenu.prevent
      />

      <v-tour
        :key="tourResetKey"
        name="purplePostTour"
        :steps="tourSteps"
        :options="tourOptions"
        :callbacks="tourCallbacks"
      >
        <template slot-scope="tour">
          <transition name="fade">
            <v-step
              v-if="tour.steps[tour.currentStep]"
              :key="tour.currentStep"
              :step="tour.steps[tour.currentStep]"
              :previous-step="tour.previousStep"
              :next-step="tour.nextStep"
              :finish="tour.finish"
              :skip="tour.skip"
              :is-first="tour.isFirst"
              :is-last="tour.isLast"
              :labels="tour.labels"
            >
              <template v-slot:header>
                <div class="v-step__header">
                  <div class="v-step__custom-top-bar text-muted">
                    <span>{{ $t('onboarding.stepCounter', { current: tour.currentStep + 1, total: tour.steps.length }) }}</span>
                  </div>
                  <div v-if="tour.steps[tour.currentStep].header && tour.steps[tour.currentStep].header.title">
                    {{ tour.steps[tour.currentStep].header.title }}
                  </div>
                </div>
              </template>
            </v-step>
          </transition>
        </template>
      </v-tour>
    </section>
  </div>
</template>

<script>

// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from '@themeConfig'
import { provideToast } from 'vue-toastification/composition'
import { watch } from '@vue/composition-api'
import useAppConfig from '@core/app-config/useAppConfig'
import { mapState } from 'vuex';

import { useWindowSize, useCssVar } from '@vueuse/core'

import store from '@/store'
import {
  buildTourSteps,
  startTourSequence,
  startTourAtStep,
  endTour,
  handleNextStep,
  handlePreviousStep,
} from '@/services/OnboardingService';

const LayoutVertical = () => import('@/layouts/vertical/LayoutVertical.vue')
const LayoutHorizontal = () => import('@/layouts/horizontal/LayoutHorizontal.vue')
const LayoutFull = () => import('@/layouts/full/LayoutFull.vue')

export default {
  components: {
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
  },
  data() {
    return {
      idleTimeAllowed: 15,
      idleTimer: null,
      tourResetKey: 0,
      // Handle callbacks. IDE may show these as unused, however these are provided by the service and used by the tour package
      tourCallbacks: {
        onSkip: () => endTour(this),
        onFinish: () => endTour(this),
        onNextStep: currentStep => handleNextStep(currentStep, this),
        onPreviousStep: currentStep => handlePreviousStep(currentStep, this),
      },
    }
  },
  computed: {
    layout() {
      if (this.$route.meta.layout === 'full') return 'layout-full'
      return `layout-${this.contentLayoutType}`
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type
    },
    ...mapState({
      isElectron: state => state.isElectron,
      isOnboarding: state => state.app.isOnboarding,
    }),
    tourOptions() {
      return {
        useKeyboardNavigation: false,
        labels: {
          buttonSkip: this.$t('onboarding.skip') || 'Skip',
          buttonPrevious: this.$t('onboarding.previous') || 'Previous',
          buttonNext: this.$t('onboarding.next') || 'Next',
          buttonStop: this.$t('onboarding.finish') || 'Finish',
        },
      }
    },
    tourSteps() {
      return buildTourSteps(this);
    },
  },
  watch: {
    isOnboarding(isNowActive) {
      if (isNowActive) {
        startTourSequence(this);
      }
    },
  },
  created() {
    this.resumeTourHandler = target => startTourAtStep(target, this);
    this.$root.$on('tour-resume', this.resumeTourHandler);
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ['primary', 'secondary', 'success', 'info', 'warning', 'danger', 'light', 'dark']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim()
    }

    // Set Theme Breakpoints
    const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl']

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2))
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout
    document.documentElement.setAttribute('dir', isRTL ? 'rtl' : 'ltr')

    this.$store.dispatch('auth/setCurrentUserFromLocalStorage');
  },
  beforeDestroy() {
    clearTimeout(this.idleTimer);
    this.$root.$off('tour-resume', this.resumeTourHandler);
  },
  setup() {
    const { skin, skinClasses } = useAppConfig()

    // If skin is dark when initialized => Add class to body
    if (skin.value === 'dark') document.body.classList.add('dark-layout')

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: 'Vue-Toastification__fade',
    })

    store.commit('app/UPDATE_WINDOW_WIDTH', window.innerWidth)
    const { width: windowWidth } = useWindowSize()
    watch(windowWidth, val => {
      store.commit('app/UPDATE_WINDOW_WIDTH', val)
    })

    return {
      skinClasses,
    }
  },
  mounted() {
    this.startInactivityTimer();
  },
  methods: {
    incrementTourResetKey() {
      this.tourResetKey += 1;
    },
    startInactivityTimer() {
      this.trackIdleTime();
      this.detectUserActivity();
    },
    detectUserActivity() {
      document.addEventListener('click', this.trackIdleTime);
      document.addEventListener('mousemove', this.trackIdleTime);
    },
    trackIdleTime() {
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }
      this.idleTimer = setTimeout(() => {
        this.logOutUser();
      }, this.idleTimeAllowed * 1000 * 60);
    },
    logOutUser() {
      this.$store.dispatch('auth/clearLocalUser');
      this.$store.commit('stats/SET_LOADED', false);
      this.$router.push('/login');
    },
  },
}
</script>
